import React from 'react';
import { useIntl, defineMessages } from 'react-intl';

import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Image from '@atlaskit/image';

import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

import { PTV2TemplateCard } from './PTV2TemplateCard.tsx';
import type { WHITEBOARD_TEMPLATE_IDS } from './mappings/whiteboardMappings.ts';
import { WHITEBOARD_TEMPLATE_TITLE_MAPPING } from './mappings/whiteboardMappings.ts';
import {
	WHITEBOARD_TEMPLATE_TO_IMG_MAPPING,
	defaultDarkImgSrc,
	defaultImgSrc,
	WHITEBOARD_TEMPLATE_TO_DARK_IMG_MAPPING,
} from './mappings/templateToImgMappings.ts';
import { usePTV2State } from './usePTV2Store.tsx';

export const i18n: {
	whiteboardTemplateType: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	whiteboardTemplateType: {
		id: 'experiment-personalized-templates-v2.whiteboard.template',
		defaultMessage: 'Whiteboard template',
		description: 'Subheading on a card if the template suggestion is for whiteboard content type',
	},
});

type PTV2WhiteboardTemplateCardType = {
	templateName: WHITEBOARD_TEMPLATE_IDS;
	onClick: () => void;
	usage: number;
};
export const PTV2WhiteboardTemplateCard = ({
	usage,
	templateName,
	onClick,
}: PTV2WhiteboardTemplateCardType): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { personalSpaceKey, personalizationHeuristic, onboardingPeriodDay, isXflow, source } =
		usePTV2State();

	const handleClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'templateCardPTV2',
				source: source ?? 'cc-onboarding-personalized-templates-v2',
				attributes: {
					templateType: 'whiteboard',
					templateId: templateName, // whiteboard template names are already the IDs
					personalizationHeuristic,
					onboardingPeriodDay,
					isXflow,
				},
			},
		}).fire();
		onClick();
		push(
			buildCreationLink({
				contentType: 'whiteboard',
				source: 'cc-onboarding-personalized-templates-v2',
				spaceKey: personalSpaceKey,
				insertTemplateWithKey: templateName,
				openTemplatePicker: false,
			}),
		);
	};

	return (
		<PTV2TemplateCard
			usage={usage}
			subTitle={formatMessage(i18n.whiteboardTemplateType)}
			icon={<WhiteboardIcon label="" />}
			onClick={handleClick}
			title={formatMessage(
				WHITEBOARD_TEMPLATE_TITLE_MAPPING[templateName as WHITEBOARD_TEMPLATE_IDS],
			)}
			image={
				<Image
					src={
						WHITEBOARD_TEMPLATE_TO_IMG_MAPPING[templateName as WHITEBOARD_TEMPLATE_IDS] ||
						defaultImgSrc
					}
					srcDark={
						WHITEBOARD_TEMPLATE_TO_DARK_IMG_MAPPING[templateName as WHITEBOARD_TEMPLATE_IDS] ||
						defaultDarkImgSrc
					}
					alt=""
				/>
			}
		/>
	);
};
